import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * `g` + letter cross-surface navigation shortcuts.
 *
 * Mirrors the Mirror layout's `g`-prefix UX so power users get a single
 * keyboard idiom across both surfaces:
 *   g m → /mirror              (Mission Control → Mirror)
 *   g h → /                    (Mirror      → Mission Control home)
 *
 * Pass a `{ key: path }` map. Honors the same guardrails as Mirror:
 * ignores when the user is typing in an editable field, when a modifier
 * is held, and after a 1s timeout.
 */

function isEditable(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
}

export default function useNavShortcuts(map) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!map) return undefined;
    let pendingG = false;
    let timer = null;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditable(document.activeElement)) return;
      if (!pendingG && e.key === 'g' && !e.shiftKey) {
        pendingG = true;
        timer = setTimeout(() => { pendingG = false; }, 1000);
        return;
      }
      if (pendingG) {
        pendingG = false;
        if (timer) { clearTimeout(timer); timer = null; }
        const target = map[e.key.toLowerCase()];
        if (target) {
          e.preventDefault();
          navigate(target);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (timer) clearTimeout(timer);
    };
  }, [navigate, map]);
}
